import * as React from "react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { GalleryItemProps } from "@/types/inertia"
import "@/styles/public-gallery.css"

// The gallery lightbox (M7 decision 10): the design-system <Dialog> showing
// one piece at a time — the full 1200 px photograph, its occasion, title and
// caption — with previous / next buttons, the arrow keys, a "2 of 5" count,
// Escape to close (Radix) and focus handed back to the piece it came from.

export function GalleryLightbox({
  items,
  openId,
  onOpenIdChange,
  onClosed,
}: {
  /** The pieces the grid is showing (after the occasion filter), in order. */
  items: GalleryItemProps[]
  /** The piece on show, or null when closed. */
  openId: number | null
  onOpenIdChange: (id: number | null) => void
  /** Called once the dialog has closed, with the last piece shown, to return focus. */
  onClosed: (id: number) => void
}) {
  const index = openId === null ? -1 : items.findIndex((item) => item.id === openId)
  const item = index >= 0 ? items[index] : null

  // Radix renders the content for a moment after `open` turns false; keep
  // the last piece so it never renders empty.
  const lastItem = React.useRef<GalleryItemProps | null>(null)
  if (item) lastItem.current = item
  const shown = item ?? lastItem.current

  const count = items.length
  const hasPrevious = index > 0
  const hasNext = index >= 0 && index < count - 1

  const step = (offset: number) => {
    const next = items[index + offset]
    if (next) onOpenIdChange(next.id)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft" && hasPrevious) {
      event.preventDefault()
      step(-1)
    } else if (event.key === "ArrowRight" && hasNext) {
      event.preventDefault()
      step(1)
    }
  }

  return (
    <Dialog open={item !== null} onOpenChange={(open) => !open && onOpenIdChange(null)}>
      {shown && (
        <DialogContent
          className="gal-lightbox"
          aria-describedby="gallery-lightbox-caption"
          onKeyDown={onKeyDown}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            onClosed(shown.id)
          }}
        >
          <div className="gal-lb-media">
            <img
              key={shown.id}
              src={shown.image_url}
              alt={shown.title}
              width={shown.width ?? undefined}
              height={shown.height ?? undefined}
              decoding="async"
            />
          </div>

          <div className="gal-lb-body">
            <DialogHeader>
              <div className="eyebrow">{shown.occasion_label}</div>
              <DialogTitle>{shown.title}</DialogTitle>
              <DialogDescription id="gallery-lightbox-caption">
                {shown.caption ?? `A bespoke bottle for ${shown.occasion_label.toLowerCase()}.`}
              </DialogDescription>
            </DialogHeader>

            <div className="gal-lb-cta">
              <Button asChild variant="ghost-hairline" size="lg">
                <Link href="/studio">Design your own</Link>
              </Button>
            </div>

            <div className="gal-lb-nav">
              <span className="gal-lb-counter" aria-live="polite">
                {index + 1} of {count}
              </span>
              {count > 1 && (
                <div className="gal-lb-steps">
                  <button
                    type="button"
                    className="gal-lb-step"
                    onClick={() => step(-1)}
                    disabled={!hasPrevious}
                    aria-label="Previous piece"
                  >
                    <Arrow direction="left" />
                  </button>
                  <button
                    type="button"
                    className="gal-lb-step"
                    onClick={() => step(1)}
                    disabled={!hasNext}
                    aria-label="Next piece"
                  >
                    <Arrow direction="right" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  )
}

function Arrow({ direction }: { direction: "left" | "right" }) {
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden="true">
      {direction === "left" ? (
        <path d="M6 1L1 6l5 5M1 6h14" stroke="currentColor" strokeWidth="1.2" />
      ) : (
        <path d="M10 1l5 5-5 5M15 6H1" stroke="currentColor" strokeWidth="1.2" />
      )}
    </svg>
  )
}
